import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EndpointService } from '../../../../../../@shared/endpoints.service';
import { CompanyResponse, CompanyServiceResponse, UserSearchResult } from '../../../../../../@shared/models/response.module';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../@core/services/toast.service';

@Component({
  selector: 'app-create-invoice-modal',
  standalone: false,
  templateUrl: './create-invoice-modal.html',
  styleUrl: './create-invoice-modal.css'
})
export class CreateInvoiceModal implements OnInit {
  @Input() company: CompanyResponse;

  form: FormGroup;
  saving: boolean = false;
  loadingServices: boolean = true;
  services: CompanyServiceResponse[] = [];
  searchResults: UserSearchResult[] = [];
  searching: boolean = false;
  searchSubject = new Subject<string>();

  selectedUser: UserSearchResult | null = null;
  useEmailOnly: boolean = false;

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private endpointService: EndpointService,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      user_email: [''],
      payment_type: ['one_time'],
      due_date: [''],
      line_items: this.fb.array([])
    });
  }

  ngOnInit() {
    this.loadServices();

    // Setup user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchUsers(query);
    });
  }

  get lineItems() {
    return this.form.get('line_items') as FormArray;
  }

  loadServices() {
    this.loadingServices = true;
    this.endpointService.csr_1(this.company.company_id).subscribe({
      next: (res) => {
        this.services = (res.data || []).filter(s => s.is_active);
        this.loadingServices = false;
      },
      error: () => {
        this.loadingServices = false;
        this.services = [];
      }
    });
  }

  onUserSearch(event: Event) {
    const query = (event.target as HTMLInputElement).value;
    this.searchSubject.next(query);
  }

  searchUsers(query: string) {
    if (!query || query.length < 2) {
      this.searchResults = [];
      return;
    }

    this.searching = true;
    this.endpointService.aqh_eve_67({ search: query }).subscribe({
      next: (res) => {
        this.searchResults = res.data || [];
        this.searching = false;
      },
      error: () => {
        this.searchResults = [];
        this.searching = false;
      }
    });
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.useEmailOnly = false;
    this.form.patchValue({ user_email: user.email });
    this.searchResults = [];
  }

  clearUser() {
    this.selectedUser = null;
    this.form.patchValue({ user_email: '' });
  }

  toggleEmailOnly() {
    this.useEmailOnly = !this.useEmailOnly;
    if (this.useEmailOnly) {
      this.selectedUser = null;
    }
  }

  addLineItem() {
    const lineItem = this.fb.group({
      type: ['service'],
      service_id: [null],
      description: [''],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unit_price_cents: [0],
      vat_rate: [0]
    });

    this.lineItems.push(lineItem);
  }

  removeLineItem(index: number) {
    this.lineItems.removeAt(index);
  }

  onServiceChange(index: number, serviceId: string) {
    if (!serviceId) return;
    const service = this.services.find(s => s.service_id === Number(serviceId));
    if (service) {
      const lineItem = this.lineItems.at(index);
      lineItem.patchValue({
        service_id: service.service_id,
        description: service.service_name,
        unit_price_cents: service.price_cents,
        vat_rate: service.vat_rate || 0
      });
    }
  }

  onTypeChange(index: number, type: string) {
    const lineItem = this.lineItems.at(index);
    if (type === 'custom') {
      lineItem.patchValue({
        service_id: null,
        description: '',
        unit_price_cents: 0,
        vat_rate: 0
      });
    }
  }

  getLineItemTotal(index: number): number {
    const lineItem = this.lineItems.at(index).value;
    const unitPrice = Number(lineItem.unit_price_cents) || 0;
    const quantity = Number(lineItem.quantity) || 1;
    const vatRate = Number(lineItem.vat_rate) || 0;
    const subtotal = unitPrice * quantity;
    const vat = subtotal * (vatRate / 100);
    return subtotal + vat;
  }

  getTotalAmount(): number {
    let total = 0;
    for (let i = 0; i < this.lineItems.length; i++) {
      total += this.getLineItemTotal(i);
    }
    return total;
  }

  formatPrice(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  close() {
    this.activeModal.dismiss();
  }

  canSubmit(): boolean {
    if (this.lineItems.length === 0) return false;
    if (!this.selectedUser && (!this.useEmailOnly || !this.form.get('user_email')?.value)) return false;
    return true;
  }

  save() {
    if (!this.canSubmit()) return;

    this.saving = true;
    const formValue = this.form.value;

    const payload: any = {
      company_id: this.company.company_id,
      payment_type: formValue.payment_type,
      line_items: formValue.line_items.map((item: any) => ({
        service_id: item.type === 'service' && item.service_id ? Number(item.service_id) : null,
        description: item.description,
        quantity: Number(item.quantity) || 1,
        unit_price_cents: Number(item.unit_price_cents) || 0,
        vat_rate: Number(item.vat_rate) || 0
      }))
    };

    if (this.selectedUser) {
      payload.user_id = this.selectedUser.user_id;
    } else {
      payload.user_email = formValue.user_email;
    }

    if (formValue.due_date && formValue.payment_type === 'one_time') {
      payload.due_date = formValue.due_date;
    }

    const handleSuccess = () => {
      this.saving = false;
      this.toastService.success(this.translateService.instant('companyProfile.createInvoiceModal.success'));
      this.activeModal.close(true);
    };

    const handleError = (err: any) => {
      console.error('Error creating invoice:', err);
      this.toastService.error(this.translateService.instant('companyProfile.createInvoiceModal.error'));
      this.saving = false;
    };

    if (formValue.payment_type === 'subscription') {
      this.endpointService.csr_9(payload).subscribe({
        next: handleSuccess,
        error: handleError
      });
    } else {
      this.endpointService.csr_6(payload).subscribe({
        next: handleSuccess,
        error: handleError
      });
    }
  }
}
